import { DynamicManager, BasicState, CustomState, Mapping } from "../common/DynamicManager";
import {
  EMPTY_VALUE,
  FieldName,
  FieldNames,
  FieldSchema,
  FieldValue,
  FieldValues,
  PreparedDynamicStructure,
  pickFields,
  prepareDynamicPropertyValue
} from "../Schema";

export enum DataTransformDirections {
  GENERAL = "general",
  FROM_GRAPHQL = "fromGraphql",
  TO_GRAPHQL = "toGraphql",
  FROM_DB = "fromDb",
  TO_DB = "toDb",
  FROM_CSV = "fromCsv",
  TO_CSV = "toCsv",
}

export interface DataTransformSchema extends Partial<
  Record<DataTransformDirections, FieldValue>
> {};

export interface DataTransformFieldState extends BasicState, DataTransformSchema {
  value: FieldValue;
}

export interface DataTransformState extends CustomState {
  direction: DataTransformDirections | typeof EMPTY_VALUE;
  values: FieldValues;
}

export const defaultDataTransformState: DataTransformState = {
  loading: true,
  direction: EMPTY_VALUE,
  values: {},
};

const DIRECTION_FIELD = "__direction__";

/**
 * Manager for handling data transformation logic based on a given schema.
 * It extends the DynamicManager to use its state management capabilities
 * while adding specific logic for handling data transformation.
 */
export class DataTransformManager extends DynamicManager<DataTransformFieldState> {

  /**
   * Custom status for DataTransformManager, which includes processing status,
   * transformed values, and any other relevant information.
   */
  customState: DataTransformState = defaultDataTransformState;

  /**
   * A field keeping the last used transformation direction.
   */
  direction: DataTransformDirections | typeof EMPTY_VALUE = EMPTY_VALUE;

  /**
   * Updates the custom state of the DataTransformManager based on the current field states.
   * It sets the processing status and gathers the current values of all fields.
   */
  async updateCustomState() {
    this.customState = {
      loading: this.loading,
      direction: this.direction,
      values: this.getValues(),
    }
  }

  /**
   *  Generates a mapping of transformations for the field.
   *
   *  @param field - field schema to generate the mapping for.
   */
  getMapping(field: FieldSchema): Mapping<DataTransformFieldState> {
    const preparedValueGetters = Object.values(DataTransformDirections)
      .reduce((getters, direction) => {

        if (field.dataTransform && field.dataTransform.hasOwnProperty(direction)) {
          getters[direction] = prepareDynamicPropertyValue(
            field.dataTransform[direction]
          );
        }
        
        return getters;
      }, {} as Record<DataTransformDirections, PreparedDynamicStructure<FieldValue>>);

    return {
      value: {
        getter: async (values: FieldValues) => {
          const direction = values[DIRECTION_FIELD] as DataTransformDirections;
          let transformedValue: FieldValue = values[field.name];
          if (preparedValueGetters.hasOwnProperty(DataTransformDirections.GENERAL)) {
            transformedValue = await preparedValueGetters[DataTransformDirections.GENERAL]
              .getter(values);
          }
          if (
            direction &&
            preparedValueGetters.hasOwnProperty(direction)
          ) {
            transformedValue = await preparedValueGetters[direction]
              .getter(
                {
                  ...values,
                  [field.name]: transformedValue,
                }
              );
          }

          return transformedValue;
        },
        fields: Array.from(new Set([
          field.name,
          DIRECTION_FIELD,
          ...Object.values(preparedValueGetters)
            .flatMap(getter => getter.fields)
        ]))
      }
    };
  }

  /**
   * Transforms the values based on the given direction.
   *
   * @param direction - The direction of the transformation.
   * @param values - The values to be transformed.
   * @returns A promise that resolves to the transformed values.
   */
  async transformValues(
    direction: DataTransformDirections,
    values: FieldValues
  ): Promise<FieldValues> {
    this.direction = direction;
    
    await this.updateState({
      ...values,
      [DIRECTION_FIELD]: direction,
    });

    return this.getValues();
  }

  /**
   * Retrieves the current values of all fields in the state.
   *
   * @returns An object containing the current values of all fields.
   */
  getValues(): FieldValues {
    const values: FieldValues = {};
    for (const fieldName in this.state) {
      values[fieldName] = this.state[fieldName].value;
    }
    return values;
  }

  /**
   * Retrieves the current value of a specific field.
   *
   * @param name - The name of the field to retrieve the value for.
   * @returns The current value of the specified field.
   */
  getFieldValue(name: FieldName): FieldValue {
    return this.state[name].value;
  }

  /**
   * Whether the field is carried by a write in this direction at all.
   *
   * A field opts out by declaring `undefined` for the direction; anything that does not
   * declare the direction is carried through untouched, and so is written.
   *
   * Read from `dataTransform` alone. Whether a field is a grid column, a CSV column or a
   * form input is a different question on a different aspect, and none of them decides
   * this one.
   *
   * @param name - The field to test.
   * @param direction - The direction the write is shaped for.
   * @returns Whether a write would include it.
   */
  private isWritten(name: FieldName, direction: DataTransformDirections): boolean {
    const dataTransform = this.schema[name]?.dataTransform;
    if (!dataTransform || !Object.prototype.hasOwnProperty.call(dataTransform, direction)) {
      return true;
    }
    return dataTransform[direction] !== undefined;
  }

  /**
   * Indexes, for every field, the fields whose value is computed from it.
   *
   * @returns Dependency name to the names that read it.
   */
  private getDependents(): Record<FieldName, FieldNames> {
    const dependents: Record<FieldName, FieldNames> = {};
    for (const [name, field] of Object.entries(this.schema)) {
      const assignedValue = field.data?.assignedValue;
      if (!assignedValue || typeof assignedValue !== "object") {
        continue;
      }
      const dependencies = (assignedValue as { fields?: FieldNames }).fields ?? [];
      for (const dependency of dependencies) {
        dependents[dependency] = [...(dependents[dependency] ?? []), name];
      }
    }
    return dependents;
  }

  /**
   * The written fields whose value follows from the given ones.
   *
   * A request that changes a building has also changed the location string, and one that
   * clears `isBookable` has cleared the five check-in fields with it. Those are
   * `DataManager` computing `data.assignedValue`, not values the request carried, so writing
   * only what was sent would leave them contradicting the fields they are derived from.
   *
   * Followed transitively, since a computed field may be read by another.
   *
   * @param names - The fields the request carried.
   * @param direction - The direction the write is shaped for.
   * @returns The additional fields to write, excluding the given ones.
   */
  getDerivedFields(names: FieldNames, direction: DataTransformDirections): FieldNames {
    const dependents = this.getDependents();
    const derived = new Set<FieldName>();
    const pending = [...names];

    while (pending.length) {
      const current = pending.shift()!;
      for (const dependent of dependents[current] ?? []) {
        if (!derived.has(dependent) && !names.includes(dependent)) {
          derived.add(dependent);
          pending.push(dependent);
        }
      }
    }

    return Array.from(derived).filter((name) => this.isWritten(name, direction));
  }

  /**
   * The fields a partial write touches: the ones the request carried, plus the ones that
   * follow from them.
   *
   * Everything else keeps whatever the record already holds. That is the point — a value
   * that makes a round trip through the schema and back is a value that can come out
   * different from how it went in, and nothing the request did not touch should be exposed
   * to that.
   *
   * @param names - The fields the request carried.
   * @param direction - The direction the write is shaped for.
   * @param exclude - Fields to keep out of the write regardless.
   * @returns The fields to write.
   */
  getWriteSet(
    names: FieldNames,
    direction: DataTransformDirections,
    exclude: FieldNames = []
  ): FieldNames {
    const excluded = new Set(exclude);
    return [...names, ...this.getDerivedFields(names, direction)]
      .filter((name) => !excluded.has(name));
  }

  /**
   * Transforms the values, then narrows the result to the fields a partial write touches.
   *
   * {@link transformValues} answers for every field in the schema, so a caller writing only
   * part of a record cannot use it directly: it would turn a computed default for an
   * untouched field into a value the caller appears to have sent.
   *
   * @param direction - The direction of the transformation.
   * @param values - The values to be transformed.
   * @param submitted - The fields the request carried.
   * @param exclude - Fields to keep out of the write regardless.
   * @returns The transformed values, narrowed to the write set.
   */
  async transformPartialValues(
    direction: DataTransformDirections,
    values: FieldValues,
    submitted: FieldNames,
    exclude: FieldNames = []
  ): Promise<FieldValues> {
    const transformed = await this.transformValues(direction, values);
    return pickFields(transformed, this.getWriteSet(submitted, direction, exclude));
  }
}
